import { Seo } from '../components/Seo';
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { DealCard } from '../components/OfferCards';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqPageSchema, breadcrumb } from '../lib/structuredData';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { openDeal } from '../lib/deals';
import { TourCard } from '../components/TourCard';
import { Hotel, Ticket, Plane, CalendarDays, ChevronRight, Home as HomeIcon, Map, Stamp } from 'lucide-react';

const buildDestFaqs = (city, dd = {}) => {
  const country = dd.country;
  const faqs = [
    {
      q: `What is the best time to visit ${city}?`,
      a: dd.best_time
        ? `The best time to visit ${city}${country ? `, ${country}` : ''} is ${dd.best_time}. Prices and crowds vary by season, so compare flight and hotel deals on FlightDeals.in before you book.`
        : `${city} is worth visiting year-round. Compare seasonal flight and hotel prices on FlightDeals.in to pick the best-value dates for your trip.`,
    },
    {
      q: `How can I find cheap flights to ${city}?`,
      a: `Use FlightDeals.in to compare fares to ${city}${dd.code ? ` (${dd.code})` : ''} across airlines and partners with our transparent FlightDeals Score — and we never add booking fees. Keeping your dates flexible usually reveals the lowest prices.`,
    },
    {
      q: `Where should I stay in ${city}?`,
      a: `Compare hotels in ${city} across our partners on FlightDeals.in to find the best price for your dates, budget and preferred area.`,
    },
    {
      q: `What are the best things to do in ${city}?`,
      a: `Browse top-rated experiences, tours and activities in ${city} on FlightDeals.in — from sightseeing and food tours to adventure and multi-day trips.`,
    },
  ];
  if (country) {
    faqs.push({
      q: `Do I need a visa to visit ${country}?`,
      a: `Visa requirements for ${country} depend on your nationality and trip length. Check the latest rules and apply through the Visa section on FlightDeals.in before you travel.`,
    });
  }
  return faqs;
};

export default function Destination() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { currency } = useApp();
  const [data, setData] = useState(null);
  const [tours, setTours] = useState([]);
  const [tripGuides, setTripGuides] = useState([]);

  useEffect(() => {
    api.destination(slug).then(({ data }) => {
      setData(data);
    }).catch(() => {
      // During build-time prerender we must NOT redirect (keep the destination URL + meta).
      if (!(typeof navigator !== 'undefined' && navigator.userAgent.includes('ReactSnap'))) navigate('/');
    });
    api.toursByDestination(slug).then(({ data }) => setTours(data.tours || [])).catch(() => setTours([]));
    api.editorialList({ destination: slug }).then(({ data }) => setTripGuides(data.itineraries || [])).catch(() => setTripGuides([]));
  }, [slug, navigate]);

  // Slug-based fallback so every /destinations/<city> URL has a unique title/canonical even
  // if live data hasn't loaded yet (e.g. during prerender when the API isn't reachable).
  const cityName = data?.destination?.city || slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const destFaqs = buildDestFaqs(cityName, data?.destination || {});
  const destSeo = (
    <Seo title={`${cityName} Travel Guide — Flights, Hotels & Things to Do`} path={`/destinations/${slug}`}
      description={((data?.destination?.tagline ? data.destination.tagline + ' ' : '')) + `Plan your trip to ${cityName}: compare flights, hotels and experiences, and discover the best things to do on FlightDeals.in.`}
      image={data?.destination?.image}
      jsonLd={[
        {
          '@context': 'https://schema.org', '@type': 'TouristDestination',
          name: cityName, description: data?.destination?.tagline || undefined, image: data?.destination?.image || undefined,
          url: `https://flightdeals.in/destinations/${slug}`,
        },
        faqPageSchema(destFaqs),
        breadcrumb([{ name: 'Home', path: '/' }, { name: 'Destinations' }, { name: cityName }]),
      ]} />
  );

  if (!data) return <Layout>{destSeo}<div className="max-w-7xl mx-auto px-4 py-20">Loading…</div></Layout>;
  const d = data.destination;

  return (
    <Layout>
      {destSeo}
      <section className="relative h-80">
        <img src={d.image} alt={d.city} className="w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 pb-8 text-primary-foreground">
            <nav className="flex items-center gap-1 text-xs text-primary-foreground/70 mb-2" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-accent flex items-center gap-1"><HomeIcon className="h-3 w-3" /> Home</Link>
              <ChevronRight className="h-3 w-3" /> <span>Destinations</span>
              <ChevronRight className="h-3 w-3" /> <span className="text-primary-foreground">{d.city}</span>
            </nav>
            <h1 className="font-display text-4xl font-semibold">{d.city}</h1>
            <p className="text-primary-foreground/85">{d.tagline}</p>
            <div className="mt-3 flex flex-wrap gap-2">{d.tags?.map((t) => <Badge key={t} className="bg-white/15 border-white/20 text-primary-foreground rounded-full">{t}</Badge>)}</div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <p className="text-lg">{d.summary}</p>
            <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground"><CalendarDays className="h-4 w-4 text-accent" /> Best time to visit: <span className="font-medium text-foreground">{d.best_time}</span></div>
            <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              <Button onClick={() => navigate(`/flights/results?origin=CCU&destination=${d.code}&trip_type=round_trip&adults=1&cabin=economy`)} className="h-12 rounded-xl bg-accent text-accent-foreground hover:bg-accent/90" data-testid="dest-flights"><Plane className="h-4 w-4 mr-1.5" /> Flights</Button>
              <Button onClick={() => navigate(`/hotels?destination_code=${d.code}`)} variant="outline" className="h-12 rounded-xl" data-testid="dest-hotels"><Hotel className="h-4 w-4 mr-1.5" /> Hotels</Button>
              <Button onClick={() => navigate(`/experiences?destination_code=${d.code}`)} variant="outline" className="h-12 rounded-xl" data-testid="dest-experiences"><Ticket className="h-4 w-4 mr-1.5" /> Experiences</Button>
              <Button onClick={() => navigate(`/tours/${slug}`)} variant="outline" className="h-12 rounded-xl" data-testid="dest-tours"><Map className="h-4 w-4 mr-1.5" /> Tours</Button>
              <Button onClick={() => navigate('/visa')} variant="outline" className="h-12 rounded-xl" data-testid="dest-visa"><Stamp className="h-4 w-4 mr-1.5" /> Visa</Button>
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-semibold mb-1">Quick facts</h3>
            <dl className="text-sm space-y-2 mt-3">
              <div className="flex justify-between"><dt className="text-muted-foreground">Country</dt><dd className="font-medium">{d.country}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Airport</dt><dd className="font-medium font-mono">{d.code}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Local currency</dt><dd className="font-medium">{d.currency}</dd></div>
            </dl>
          </div>
        </div>

        {tripGuides.length > 0 && (
          <div className="mt-10" data-testid="dest-trip-styles">
            <h2 className="font-display text-2xl font-semibold mb-1">Choose your {d.city} trip style</h2>
            <p className="text-sm text-muted-foreground mb-4">Hand-built {d.city} itineraries for every kind of traveller — pick the one that matches your trip.</p>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tripGuides.map((g) => (
                <Link key={g.slug} to={`/itineraries/${g.slug}`} className="group rounded-2xl border border-border bg-card overflow-hidden card-elevate flex flex-col" data-testid={`dest-guide-${g.slug}`}>
                  <div className="h-40 relative overflow-hidden">
                    <img src={g.cover} alt={g.coverAlt} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/80 to-transparent" />
                    <Badge className="absolute top-2 left-2 bg-accent text-accent-foreground rounded-full text-[10px]">{g.badge}</Badge>
                  </div>
                  <div className="p-4 flex flex-col flex-1">
                    <p className="font-display font-semibold leading-tight line-clamp-2">{g.title}</p>
                    <span className="mt-auto pt-3 text-accent font-semibold text-sm inline-flex items-center gap-1">Read guide <ChevronRight className="h-4 w-4" /></span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {data.deals?.length > 0 && (
          <div className="mt-10">
            <h2 className="font-display text-2xl font-semibold mb-4">Deals to {d.city}</h2>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {data.deals.map((deal) => <DealCard key={deal.id} deal={deal} currency={currency} onViewDeal={(x) => openDeal({ type: x.type, offer: x, surface: 'destination_page', source: `/destinations/${slug}` })} />)}
            </div>
          </div>
        )}
        {tours.length > 0 && (
          <div className="mt-10" data-testid="dest-tours-lane">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-2xl font-semibold flex items-center gap-2"><Map className="h-6 w-6 text-accent" /> Multi-day tours in {d.city}</h2>
              <Link to={`/tours/${slug}`} className="text-sm text-accent font-medium">View all →</Link>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {tours.slice(0, 3).map((t) => <TourCard key={t.tour_id} t={t} currency={currency} />)}
            </div>
          </div>
        )}

        <div className="mt-10" data-testid="dest-faq">
          <h2 className="font-display text-2xl font-semibold mb-4">Frequently asked questions about {d.city}</h2>
          <Accordion type="single" collapsible className="rounded-2xl border border-border bg-card divide-y divide-border">
            {destFaqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="px-4 border-b-0">
                <AccordionTrigger className="text-left font-medium hover:no-underline" data-testid={`dest-faq-q-${i}`}>{f.q}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground" data-testid={`dest-faq-a-${i}`}>{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </Layout>
  );
}
